import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Sketch from '@uiw/react-color-sketch';
import { Button, Overlay } from 'react-bootstrap';
import './LomavisSketchColorPicker.css';

export type LomavisSketchColorPickerProps = {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  compact?: boolean;
};

/** Hex-only Sketch picker rendered in a viewport-level, accessible popover. */
export const LomavisSketchColorPicker: React.FC<
  LomavisSketchColorPickerProps
> = ({ value, onChange, ariaLabel, disabled = false, compact = false }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [draftValue, setDraftValue] = useState(value);
  const draftValueRef = useRef(value);
  const committedValueRef = useRef(value);
  const releaseCommitTimer = useRef<number>();
  const target = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const popupId = `lomavis-sketch-color-picker-${useId().replace(/:/g, '')}`;

  const commitDraft = useCallback(() => {
    if (draftValueRef.current === committedValueRef.current) return;
    committedValueRef.current = draftValueRef.current;
    onChange(draftValueRef.current);
  }, [onChange]);

  const closePicker = useCallback(() => {
    commitDraft();
    setShowPicker(false);
    window.requestAnimationFrame(() => target.current?.focus());
  }, [commitDraft]);

  const cancelPicker = useCallback(() => {
    setDraftValue(value);
    draftValueRef.current = value;
    committedValueRef.current = value;
    setShowPicker(false);
    window.requestAnimationFrame(() => target.current?.focus());
  }, [value]);

  const scheduleReleaseCommit = useCallback(() => {
    window.clearTimeout(releaseCommitTimer.current);
    // UIW presets publish their color from `click`, which fires after
    // `mouseup`/`touchend`. Defer so both drag and click changes are present.
    releaseCommitTimer.current = window.setTimeout(commitDraft, 0);
  }, [commitDraft]);

  useEffect(() => {
    setDraftValue(value);
    draftValueRef.current = value;
    committedValueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!showPicker) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      cancelPicker();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [cancelPicker, showPicker]);

  useEffect(() => {
    if (!showPicker) return undefined;
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      const eventTarget = event.target;
      if (!(eventTarget instanceof Node)) return;
      if (
        target.current?.contains(eventTarget) ||
        popup.current?.contains(eventTarget)
      )
        return;
      closePicker();
    };
    // Bootstrap's rootClose can see the opening click as outside the portaled
    // overlay in the Django application shell. Capture later pointer events
    // ourselves, after the initial click has completed.
    document.addEventListener('pointerdown', closeOnOutsidePointerDown);
    return () =>
      document.removeEventListener('pointerdown', closeOnOutsidePointerDown);
  }, [closePicker, showPicker]);

  useEffect(() => {
    if (!showPicker) return undefined;
    window.addEventListener('mouseup', scheduleReleaseCommit);
    window.addEventListener('touchend', scheduleReleaseCommit);
    return () => {
      window.removeEventListener('mouseup', scheduleReleaseCommit);
      window.removeEventListener('touchend', scheduleReleaseCommit);
      window.clearTimeout(releaseCommitTimer.current);
    };
  }, [scheduleReleaseCommit, showPicker]);

  useEffect(() => {
    if (disabled && showPicker) cancelPicker();
  }, [cancelPicker, disabled, showPicker]);

  return (
    <div className={compact ? 'lomavis-sketch-color-picker-compact' : ''}>
      <Button
        ref={target}
        type="button"
        variant="light"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={showPicker}
        aria-controls={showPicker ? popupId : undefined}
        onClick={() => {
          if (showPicker) {
            closePicker();
            return;
          }
          setDraftValue(value);
          draftValueRef.current = value;
          committedValueRef.current = value;
          setShowPicker(true);
        }}
        className={
          compact
            ? 'lomavis-sketch-color-picker-compact-toggle bg-white shadow-none'
            : 'lomavis-sketch-color-picker-toggle d-flex w-100 align-items-center gap-2 rounded-3 border bg-white px-3 py-2 text-start shadow-none'
        }
      >
        {compact ? (
          <span
            className="lomavis-sketch-color-picker-icon"
            style={{ borderBottomColor: value }}
          >
            A
          </span>
        ) : (
          <>
            <span
              className="lomavis-sketch-color-picker-swatch"
              style={{ backgroundColor: value }}
            />
            <span className="text-uppercase">{value}</span>
          </>
        )}
      </Button>
      <Overlay
        target={target.current}
        show={showPicker}
        placement={compact ? 'bottom-end' : 'bottom-start'}
        popperConfig={{ strategy: 'fixed' }}
        onEntered={() =>
          popup.current?.querySelector<HTMLElement>('input, button')?.focus()
        }
      >
        {({
          ref: overlayRef,
          placement,
          arrowProps: _arrowProps,
          show: _show,
          popper: _popper,
          className: overlayClassName,
          ...overlayProps
        }) => (
          <div
            {...overlayProps}
            ref={(node) => {
              popup.current = node;
              overlayRef(node);
            }}
            id={popupId}
            role="dialog"
            aria-label={ariaLabel}
            data-popper-placement={placement}
            className={`${
              typeof overlayClassName === 'string' ? overlayClassName : ''
            } lomavis-sketch-color-picker-menu border-0 bg-white p-2 shadow`}
          >
            <Sketch
              color={draftValue}
              disableAlpha
              width={260}
              onChange={(color) => {
                setDraftValue(color.hex);
                draftValueRef.current = color.hex;
              }}
            />
          </div>
        )}
      </Overlay>
    </div>
  );
};
